import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';
import FieldLabel from '../components/FieldLabel';
import { getFieldLabel } from '../utils/formHelpers';
import { outlinedFieldProps } from '../utils/fieldStyles';
import { useAuth } from "auth/Auth";

const filter = createFilterOptions();

const slugify = (str) =>
    str
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

const toTitleCase = (value) =>
    value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// In-memory cache — keeps re-renders / step navigation from refetching
// the same endpoint repeatedly within a session.
const optionsCache = {};

const ConfigurableDropdownField = ({ field, data, change, hasError, errorMessage, resolveFieldOptions }) => {

    const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();

    const endpointKey = field.optionsEndpointKey;
    const staticOptions = resolveFieldOptions?.(field) || field.options || [];

    const [remoteOptions, setRemoteOptions] = useState(optionsCache[endpointKey] || []);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);

    useEffect(() => {
        if (!endpointKey || !axiosInstance || !isAuthReady || !isAuthenticated) return undefined;
        if (optionsCache[endpointKey]) {
            setRemoteOptions(optionsCache[endpointKey]);
            setFetchError(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setFetchError(false);

        axiosInstance.get(`/configurable-dropdown/${endpointKey}/options`)
            .then((res) => {
                if (cancelled) return;
                optionsCache[endpointKey] = res.data || [];
                setRemoteOptions(res.data || []);
                setFetchError(false);
            })
            .catch(() => {
                if (!cancelled) setFetchError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [endpointKey, axiosInstance, isAuthReady, isAuthenticated]);

    // Merge defaults (from stepsConfig) + remote (DB-distinct custom values),
    // de-duped by value. Defaults win on collision so labels stay consistent.
    const options = useMemo(() => {
        const map = new Map();
        staticOptions.forEach((opt) => map.set(opt.value, opt));
        remoteOptions.forEach((opt) => {
            if (!map.has(opt.value)) map.set(opt.value, opt);
        });
        return Array.from(map.values());
    }, [staticOptions, remoteOptions]);

    const currentValue = data[field.key]
        ? options.find((o) => o.value === data[field.key]) || {
        value: data[field.key],
        label: toTitleCase(data[field.key]),
    }
        : null;

    const handleChange = useCallback(
        (event, newValue) => {
            if (!newValue) {
                change(field.key, '');
                return;
            }
            if (typeof newValue === 'object') {
                // user clicked the synthetic "Add "X"" row
                if (newValue.inputValue) {
                    const slug = slugify(newValue.inputValue);
                    if (!slug) return;
                    const created = { value: slug, label: toTitleCase(slug) };
                    optionsCache[endpointKey] = [...(optionsCache[endpointKey] || []), created];
                    setRemoteOptions((prev) => [...prev, created]);
                    change(field.key, slug);
                    return;
                }
                change(field.key, newValue.value);
                return;
            }
            // raw string committed via freeSolo without picking a row
            const slug = slugify(newValue);
            change(field.key, slug);
        },
        [change, field.key, endpointKey]
    );

    return (
        <Autocomplete
            freeSolo
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            loading={loading}
            options={options}
            value={currentValue}
            getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.inputValue) return option.inputValue;
                return option.label || '';
            }}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            filterOptions={(opts, params) => {
                const filtered = filter(opts, params);
                const { inputValue } = params;
                const isExisting = opts.some(
                    (opt) => inputValue.trim().toLowerCase() === opt.label.toLowerCase()
                );
                if (inputValue !== '' && !isExisting) {
                    filtered.push({ inputValue, label: `Add "${inputValue}"` });
                }
                return filtered;
            }}
            onChange={handleChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...outlinedFieldProps}
                    label={<FieldLabel label={getFieldLabel(field)} tooltip={field.tooltip} />}
                    error={hasError}
                    helperText={
                        hasError
                            ? (errorMessage || 'This field is required')
                            : fetchError && staticOptions.length === 0
                                ? 'Could not load existing options — you can still type a new one.'
                                : ''
                    }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

ConfigurableDropdownField.propTypes = {
    field: PropTypes.shape({
        key: PropTypes.string.isRequired,
        optionsEndpointKey: PropTypes.string,
        options: PropTypes.array,
        tooltip: PropTypes.string,
    }).isRequired,
    data: PropTypes.object.isRequired,
    change: PropTypes.func.isRequired,
    hasError: PropTypes.bool,
    errorMessage: PropTypes.string,
    resolveFieldOptions: PropTypes.func,
};

export default ConfigurableDropdownField;